import {ModulesEnum} from './modules.enum';
import {RolesEnum} from './roles.enum';
import {AccountRoleItemModel} from '../../api/models/account-role-item-model';
import {MenuGroupEnum} from './menu-group.enum';

export interface ModuleDefinition {
  name: string;
  description: string;
  key: string;
  order: number;
  group: MenuGroupEnum;
  permissionPrefix: string;
  permissions?: PermissionDefinition[];
  additionalPermissions?: AdditionalPermissionDefinition[];
}

export interface PermissionDefinition {
  name: string;
  description: string;
  permissionKey: string;
  selected?: boolean;
}

export interface AdditionalPermissionDefinition {
  name: string;
  value?: string;
  inputType: string;
  inputName: string;
  description: string;
  permissionKey: string;
  selected?: boolean;
}

export class RolesDefinition {

  public static allRoles: ModuleDefinition[] = [
    {
      name: 'Profile',
      description: 'User account profile settings and information',
      key: ModulesEnum.UsersModule,
      group: MenuGroupEnum.SettingsGroup,
      permissionPrefix: 'Profile',
      order: 1,
      permissions: [
        {name: 'Blocked', description: 'Disable profile module', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view account profile', permissionKey: RolesEnum.AccountProfile_View},
        {name: 'View/Edit', description: 'Allow to view/modify account profile', permissionKey: RolesEnum.AccountProfile_Edit},
      ]
    },
    {
      name: 'Users',
      description: 'View, edit, and manage account\'s users',
      key: ModulesEnum.AccountUsersModule,
      group: MenuGroupEnum.SettingsGroup,
      permissionPrefix: 'AccountUsers',
      order: 2,
      permissions: [
        {name: 'Blocked', description: 'Disable users module', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view users', permissionKey: RolesEnum.AccountUsers_View},
        {name: 'View/Edit', description: 'Allow to view/modify users', permissionKey: RolesEnum.AccountUsers_Edit},
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete users',
          permissionKey: RolesEnum.AccountUsers_Delete
        },
      ]
    },
    {
      name: 'Roles',
      description: 'View, edit, and manage account\'s roles',
      key: ModulesEnum.AccountRolesModule,
      group: MenuGroupEnum.SettingsGroup,
      permissionPrefix: 'AccountRoles',
      order: 3,
      permissions: [
        {name: 'Blocked', description: 'Disable roles module', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view roles', permissionKey: RolesEnum.AccountRoles_View},
        {name: 'View/Edit', description: 'Allow to view/modify roles', permissionKey: RolesEnum.AccountRoles_Edit},
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete roles',
          permissionKey: RolesEnum.AccountRoles_Delete
        },
      ]
    },
    {
      name: 'Company Settings',
      description: 'View, edit, and manage company\'s settings',
      key: ModulesEnum.AccountSettingsModule,
      group: MenuGroupEnum.SettingsGroup,
      permissionPrefix: 'AccountSettings',
      order: 4,
      permissions: [
        {name: 'Blocked', description: 'Disable settings module', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view settings', permissionKey: RolesEnum.AccountSettings_View},
        {name: 'View/Edit', description: 'Allow to view/modify settings', permissionKey: RolesEnum.AccountSettings_Edit},
      ]
    },
    {
      name: 'Company Retention Settings',
      description: 'View, edit, and manage company\'s retention settings',
      key: ModulesEnum.AccountStorageModule,
      group: MenuGroupEnum.SettingsGroup,
      permissionPrefix: 'AccountStorage',
      order: 4,
      permissions: [
        {name: 'Blocked', description: 'Disable retention settings module', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view retention settings', permissionKey: RolesEnum.AccountStorage_View},
        {name: 'View/Edit', description: 'Allow to view/modify retention settings', permissionKey: RolesEnum.AccountStorage_Edit},
      ]
    },
    {
      name: 'Company Audit',
      description: 'View company\'s usage logs',
      key: ModulesEnum.AuditModule,
      group: MenuGroupEnum.SettingsGroup,
      permissionPrefix: 'Audit',
      order: 5,
      permissions: [
        {name: 'Blocked', description: 'Disable Audit module', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Audit records', permissionKey: RolesEnum.Audit_View},
      ]
    },
    {
      name: 'Analytics Additional Access',
      description: 'Accounts that will be visible to a user',
      key: ModulesEnum.ReportsModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'Reports_Extensions',
      order: 3,
      additionalPermissions: [
        {
          name: 'Additional Agents',
          description: 'Additional agents that will be visible in analytics',
          permissionKey: RolesEnum.Reports_Extensions,
          inputType: 'PbxExtensionsInputComponent',
          inputName: 'Reports_Extensions'
        },
        {
          name: 'Access to Groups', description: 'Groups that will be visible in analytics',
          permissionKey: RolesEnum.Reports_Groups, inputType: 'PbxGroupsInputComponent', inputName: 'Reports_Groups'
        },
        {
          name: 'Additional Departments',
          description: 'Additional departments that will be visible in analytics',
          permissionKey: RolesEnum.Reports_Departments,
          inputType: 'PbxDepartmentsInputComponent',
          inputName: 'Reports_Departments'
        },
        {
          name: 'Other Accounts',
          description: 'Other Accounts like auto attendants will be visible in analytics',
          permissionKey: RolesEnum.Reports_OtherAccounts,
          inputType: 'PbxOtherAccountsInputComponent',
          inputName: 'Reports_OtherAccounts'
        },
        {
          name: 'Excluded Agents',
          description: 'Agents which you want to exclude from Analytics',
          permissionKey: RolesEnum.Reports_Exclude_Extensions,
          inputType: 'PbxExtensionsInputComponent',
          inputName: 'Reports_Exclude_Extensions',
        },
      ]
    },
    {
      name: 'Basic Call Logs (legacy)',
      description: 'View and export your call logs',
      key: ModulesEnum.BasicCallLogsModule,
      group: MenuGroupEnum.CallLogsGroup,
      permissionPrefix: 'BasicCallLogs',
      order: 4,
      permissions: [
        {name: 'Blocked', description: 'Disable call logs', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view call logs', permissionKey: RolesEnum.BasicCallLogs_View},
        {name: 'View/Download', description: 'Allow to view/download call logs', permissionKey: RolesEnum.BasicCallLogs_Edit},
      ]
    },
    {
      name: 'Main Dashboard',
      description: 'Built-in dashboard of phone activity in a single view',
      key: ModulesEnum.ActivityDashboardV2Module,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'ActivityDashboardV2',
      order: 1,
      permissions: [
        {name: 'Blocked', description: 'Disable main dashboard', permissionKey: 'none'},
        {
          name: 'View',
          description: 'Allow to view main dashboard',
          permissionKey: RolesEnum.ActivityDashboard_View
        }
      ]
    },
    {
      name: 'Call Logs',
      description: 'Review detailed information about calls over a selected time frame',
      key: ModulesEnum.CallLogsV2Module,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'CallLogsV2',
      order: 2,
      permissions: [
        {name: 'Blocked', description: 'Disable call logs', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view call logs', permissionKey: RolesEnum.CallLogs_View},
        {name: 'View/Download', description: 'Allow to view/download call logs', permissionKey: RolesEnum.CallLogs_Edit},
      ]
    },
    {
      name: 'Agents Summary',
      description: 'Overview of your agents activity over a selected period of time',
      key: ModulesEnum.UsersSummaryV2Module,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'UsersSummaryV2',
      order: 3,
      permissions: [
        {name: 'Blocked', description: 'Disable agents list', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view agents list', permissionKey: RolesEnum.UsersSummary_View},
        {name: 'View/Download', description: 'Allow to view/download agents list', permissionKey: RolesEnum.UsersSummary_Edit},
      ]
    },
    {
      name: 'Groups Summary',
      description: 'Review your call groups activity over a selected period of time',
      key: ModulesEnum.GroupsSummaryV2Module,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'GroupsSummaryV2',
      order: 4,
      permissions: [
        {name: 'Blocked', description: 'Disable groups list', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view groups list', permissionKey: RolesEnum.GroupsSummary_View},
        {
          name: 'View/Download',
          description: 'Allow to view/download groups list',
          permissionKey: RolesEnum.GroupsSummary_Edit
        },
      ]
    },
    {
      name: 'Main Dashboard (legacy)',
      description: 'Built-in dashboard of phone activity in a single view',
      key: ModulesEnum.ActivityDashboardModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'ActivityDashboard',
      order: 5,
      permissions: [
        {name: 'Blocked', description: 'Disable main dashboard', permissionKey: 'none'},
        {
          name: 'View',
          description: 'Allow to view main dashboard',
          permissionKey: RolesEnum.ActivityDashboard_View
        }
      ]
    },
    {
      name: 'Call Logs (legacy)',
      description: 'Review detailed information about calls over a selected time frame',
      key: ModulesEnum.CallLogsModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'CallLogs',
      order: 6,
      permissions: [
        {name: 'Blocked', description: 'Disable call logs', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view call logs', permissionKey: RolesEnum.CallLogs_View},
        {name: 'View/Download', description: 'Allow to view/download call logs', permissionKey: RolesEnum.CallLogs_Edit},
      ]
    },
    {
      name: 'Agents Summary (legacy)',
      description: 'Overview of your agents activity over a selected period of time',
      key: ModulesEnum.UsersSummaryModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'UsersSummary',
      order: 7,
      permissions: [
        {name: 'Blocked', description: 'Disable agents list', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view agents list', permissionKey: RolesEnum.UsersSummary_View},
        {name: 'View/Download', description: 'Allow to view/download agents list', permissionKey: RolesEnum.UsersSummary_Edit},
      ]
    },
    {
      name: 'Agent Groups Dashboard (legacy)',
      description: 'Agent Groups Dashboard Module',
      key: ModulesEnum.GroupsDashboardModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'GroupsDashboard',
      order: 8,
      permissions: [
        {name: 'Blocked', description: 'Disable agent groups dashboard', permissionKey: 'none'},
        {
          name: 'View',
          description: 'Allow to view agent groups dashboard',
          permissionKey: RolesEnum.GroupsDashboard_View
        }
      ]
    },
    {
      name: 'Groups Summary (legacy)',
      description: 'Review your call groups activity over a selected period of time',
      key: ModulesEnum.GroupsSummaryModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'GroupsSummary',
      order: 9,
      permissions: [
        {name: 'Blocked', description: 'Disable groups list', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view groups list', permissionKey: RolesEnum.GroupsSummary_View},
        {
          name: 'View/Download',
          description: 'Allow to view/download groups list',
          permissionKey: RolesEnum.GroupsSummary_Edit
        },
      ]
    },
    {
      name: 'Hunt Groups Dashboard (legacy)',
      description: 'Hunt Groups Dashboard Module',
      key: ModulesEnum.HuntGroupsDashboardModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'HuntGroupsDashboard',
      order: 10,
      permissions: [
        {name: 'Blocked', description: 'Disable hunt groups dashboard', permissionKey: 'none'},
        {
          name: 'View',
          description: 'Allow to view hunt groups dashboard',
          permissionKey: RolesEnum.HuntGroupsDashboardView
        }
      ]
    },
    {
      name: 'Custom Reports',
      description: 'Custom Reports Module',
      key: ModulesEnum.CustomReportsModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'CustomReports',
      order: 11,
      permissions: [
        {
          name: 'Blocked',
          description: 'Disable custom reports',
          permissionKey: 'none'
        },
        {
          name: 'View',
          description: 'Allow to view custom reports',
          permissionKey: RolesEnum.CustomReportsView
        },
        {
          name: 'View/Edit',
          description: 'Allow to view/edit custom reports',
          permissionKey: RolesEnum.CustomReportsEdit
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/edit/delete custom reports',
          permissionKey: RolesEnum.CustomReportsDelete
        }
      ]
    },
    {
      name: 'Surveys',
      description: 'View surveys reports and analytics',
      key: ModulesEnum.SurveysModule,
      group: MenuGroupEnum.ReportsGroup,
      permissionPrefix: 'Surveys',
      order: 11,
      permissions: [
        {name: 'Blocked', description: 'Disable surveys module', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view surveys reports', permissionKey: RolesEnum.Surveys_View},
      ],
    },
    {
      name: 'Call Recording',
      description: 'Listen, download, and delete your call recordings',
      key: ModulesEnum.RecordingsModule,
      group: MenuGroupEnum.RecordingsGroup,
      permissionPrefix: 'Recordings',
      order: 11,
      permissions: [
        {name: 'Blocked', description: 'Disable call recordings module', permissionKey: 'none'},
        {name: 'Listen', description: 'Allow to view call recordings', permissionKey: RolesEnum.Recordings_View},
        {
          name: 'Listen/Download',
          description: 'Allow to view/modify call recordings',
          permissionKey: RolesEnum.Recordings_Edit
        },
        {
          name: 'Listen/Download/Delete',
          description: 'Allow to view/modify/delete call recordings',
          permissionKey: RolesEnum.Recordings_Delete
        },
      ],
    },
    {
      name: 'Call Recording Additional Access',
      description: 'Accounts that will be visible to a user',
      key: ModulesEnum.RecordingsExtensionsModule,
      group: MenuGroupEnum.RecordingsGroup,
      permissionPrefix: 'Recordings_Extensions',
      order: 11,
      additionalPermissions: [
        {
          name: 'Additional Agents',
          description: 'Additional agents that will be visible for call recordings',
          permissionKey: RolesEnum.Recordings_Extensions,
          inputType: 'PbxExtensionsInputComponent',
          inputName: 'Recordings_Extensions',
        },
        {
          name: 'Access to Groups', description: 'Groups that will be visible for call recordings',
          permissionKey: RolesEnum.Recordings_Groups, inputType: 'PbxGroupsInputComponent', inputName: 'Recordings_Groups'
        },
        {
          name: 'Additional Departments',
          description: 'Additional departments that will be visible for call recordings',
          permissionKey: RolesEnum.Recordings_Departments,
          inputType: 'PbxDepartmentsInputComponent',
          inputName: 'Recordings_Departments',
        },
        {
          name: 'Other Accounts',
          description: 'Other Accounts like auto attendants will be visible in analytics',
          permissionKey: RolesEnum.Recordings_OtherAccounts,
          inputType: 'PbxOtherAccountsInputComponent',
          inputName: 'Recordings_OtherAccounts'
        },
        {
          name: 'Excluded Agents',
          description: 'Agents which you want to exclude from seeing call recordings',
          permissionKey: RolesEnum.Recordings_Exclude_Extensions,
          inputType: 'PbxExtensionsInputComponent',
          inputName: 'Recordings_Exclude_Extensions',
        },
      ]
    },
    {
      name: 'Phone Numbers',
      description: 'View, edit, and manage your phone numbers. You can also set Caller ID Name and SMS settings',
      key: ModulesEnum.PhoneNumbersModule,
      group: MenuGroupEnum.PhoneNumbersGroup,
      permissionPrefix: 'PhoneNumbers',
      order: 12,
      permissions: [
        {name: 'Blocked', description: 'Disable phone numbers', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view phone numbers', permissionKey: RolesEnum.PhoneNumbers_View},
        {
          name: 'View/Edit',
          description: 'Allow to view/modify phone numbers',
          permissionKey: RolesEnum.PhoneNumbers_Edit
        },
        {
          name: 'View/Edit/Delete', description: 'Allow to view/modify/delete phone numbers',
          permissionKey: RolesEnum.PhoneNumbers_Delete
        },
      ],
      additionalPermissions: [
        {
          name: 'Assign Users',
          description: 'Assign User to Phone Number',
          permissionKey: RolesEnum.PhoneNumbers_AssignUser,
          inputType: 'CheckboxInputComponent',
          inputName: 'PhoneNumbers_AssignUser',
        },
      ]
    },
    {
      name: 'Phone Numbers Export',
      description: 'Manage who can export your phone numbers information.',
      key: ModulesEnum.PhoneNumbersExportModule,
      group: MenuGroupEnum.PhoneNumbersGroup,
      permissionPrefix: 'PhoneNumbersExport',
      order: 12,
      permissions: [
        {name: 'Blocked', description: 'Disable phone numbers export', permissionKey: 'none'},
        {name: 'Export', description: 'Allow to export phone numbers', permissionKey: RolesEnum.PhoneNumbers_Export},
      ]
    },
    {
      name: 'Basic SMS',
      description: 'Send and receive SMS from your business phone number',
      key: ModulesEnum.MessagingModule,
      group: MenuGroupEnum.MessagingGroup,
      permissionPrefix: 'Messaging',
      order: 12,
      permissions: [
        {name: 'Blocked', description: 'Disable Basic SMS', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Basic SMS settings', permissionKey: RolesEnum.Messaging_View},
        {
          name: 'View/Edit',
          description: 'Allow to view/modify Basic SMS settings',
          permissionKey: RolesEnum.Messaging_Edit
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Basic SMS settings and messages',
          permissionKey: RolesEnum.Messaging_Delete
        },
      ]
    },
    {
      name: 'Pro Business SMS',
      description: 'Send and receive SMS and MMS from your business phone number with advanced sms features',
      key: ModulesEnum.ProMessagingModule,
      group: MenuGroupEnum.ProMessagingGroup,
      permissionPrefix: 'ProMessaging',
      order: 12,
      permissions: [
        {name: 'Blocked', description: 'Disable Pro Business SMS', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Pro Business SMS settings', permissionKey: RolesEnum.ProMessaging_View},
        {
          name: 'View/Edit',
          description: 'Allow to view/modify Pro Business SMS settings',
          permissionKey: RolesEnum.ProMessaging_Edit
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Pro Business SMS settings and messages',
          permissionKey: RolesEnum.ProMessaging_Delete
        },
      ]
    },
    {
      name: 'Message Broadcasting',
      description: 'Send broadcasting message to your contacts',
      key: ModulesEnum.MessageBroadcastingModule,
      group: MenuGroupEnum.ProMessagingGroup,
      permissionPrefix: 'MessageBroadcasting',
      order: 12,
      permissions: [
        {name: 'Blocked', description: 'Disable message broadcasting', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view broadcasting messages', permissionKey: RolesEnum.MessageBroadcasting_View},
        {
          name: 'View/Edit',
          description: 'Allow to view/modify broadcasting messages',
          permissionKey: RolesEnum.MessageBroadcasting_Edit
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete broadcasting messages',
          permissionKey: RolesEnum.MessageBroadcasting_Delete
        },
      ]
    },
    {
      name: 'Pro Business SMS Automation',
      description: 'Set up Auto reply, keywords for Business SMS',
      key: ModulesEnum.MessagingAutomationModule,
      group: MenuGroupEnum.ProMessagingGroup,
      permissionPrefix: 'MessagingAutomation',
      order: 12,
      permissions: [
        {name: 'Blocked', description: 'Disable Business SMS Automation', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Business SMS automation settings', permissionKey: RolesEnum.MessagingAutomation_View},
        {
          name: 'View/Edit',
          description: 'Allow to view/modify Business SMS automation settings',
          permissionKey: RolesEnum.MessagingAutomation_Edit,
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Business SMS automation settings',
          permissionKey: RolesEnum.MessagingAutomation_Delete,
        },
      ],
    },
    {
      name: 'Pro Business SMS Templates',
      description: 'Set up templates for Pro Business SMS',
      key: ModulesEnum.MessagingTemplateModule,
      group: MenuGroupEnum.ProMessagingGroup,
      permissionPrefix: 'MessagingTemplate',
      order: 13,
      permissions: [
        {name: 'Blocked', description: 'Disable Pro Business SMS Templates', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Pro Business SMS Templates', permissionKey: RolesEnum.MessagingTemplateView},
        {
          name: 'View/Edit',
          description: 'Allow to view/modify Pro Business SMS templates',
          permissionKey: RolesEnum.MessagingTemplateEdit,
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Pro Business SMS templates',
          permissionKey: RolesEnum.MessagingTemplateDelete,
        },
      ],
    },
    {
      name: 'Pro Business SMS Private Templates',
      description: 'Set up templates for Pro Business SMS',
      key: ModulesEnum.MessagingPrivateTemplateModule,
      group: MenuGroupEnum.ProMessagingGroup,
      permissionPrefix: 'MessagingPrivateTemplate',
      order: 13,
      permissions: [
        {name: 'Blocked', description: 'Disable Pro Business SMS Private Templates', permissionKey: 'none'},
        {
          name: 'View',
          description: 'Allow to view Pro Business SMS Private Templates',
          permissionKey: RolesEnum.MessagingPrivateTemplateView
        },
        {
          name: 'View/Edit',
          description: 'Allow to view/modify Pro Business SMS Private templates',
          permissionKey: RolesEnum.MessagingPrivateTemplateEdit,
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Pro Business SMS Private templates',
          permissionKey: RolesEnum.MessagingPrivateTemplateDelete,
        },
      ],
    },
    {
      name: 'Pro Business WhatsApp Templates',
      description: 'Set up WhatsApp templates for Pro Business SMS',
      key: ModulesEnum.MessagingWhatsAppTemplateModule,
      group: MenuGroupEnum.ProMessagingGroup,
      permissionPrefix: 'MessagingWhatsAppTemplate',
      order: 13,
      permissions: [
        {name: 'Blocked', description: 'Disable Pro Business WhatsApp Templates', permissionKey: 'none'},
        {
          name: 'View',
          description: 'Allow to view Pro Business WhatsApp Templates',
          permissionKey: RolesEnum.MessagingWhatsAppTemplateView
        },
        {
          name: 'View/Edit',
          description: 'Allow to view/modify Pro Business WhatsApp templates',
          permissionKey: RolesEnum.MessagingWhatsAppTemplateEdit,
        },
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Pro WhatsApp Private templates',
          permissionKey: RolesEnum.MessagingWhatsAppTemplateDelete,
        },
      ],
    },
    {
      name: 'Pro Business SMS Reports',
      description: 'View Pro Business SMS Reports',
      key: ModulesEnum.ProMessagingReportsModule,
      group: MenuGroupEnum.ProMessagingGroup,
      permissionPrefix: 'ProMessagingReports',
      order: 14,
      permissions: [
        {name: 'Blocked', description: 'Disable Pro Business SMS Reports', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Pro Business SMS Reports', permissionKey: RolesEnum.ProMessagingReports_View},
      ],
    },
    {
      name: 'Fax',
      description: 'View, send, delete your faxes',
      key: ModulesEnum.FaxModule,
      group: MenuGroupEnum.FaxGroup,
      permissionPrefix: 'Fax',
      order: 14,
      permissions: [
        {name: 'Blocked', description: 'Disable Fax', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view incoming/outgoing faxes', permissionKey: RolesEnum.Fax_View},
        {name: 'View/Send', description: 'Allow to view/send faxes', permissionKey: RolesEnum.Fax_Edit},
        {
          name: 'View/Send/Delete',
          description: 'Allow to view/send/delete faxes',
          permissionKey: RolesEnum.Fax_Delete
        },
      ]
    },
    {
      name: 'Fax Reports',
      description: 'View Fax reports',
      key: ModulesEnum.FaxReportModule,
      group: MenuGroupEnum.FaxGroup,
      permissionPrefix: 'FaxReport',
      order: 14,
      permissions: [
        {name: 'Blocked', description: 'Disable Fax Reports', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Fax reports', permissionKey: RolesEnum.FaxReportView},
      ]
    },
    {
      name: 'Wallboard',
      description: 'Live wallboard of groups activity',
      group: MenuGroupEnum.PbxGroup,
      key: ModulesEnum.PbxWallboardModule,
      permissionPrefix: 'PbxWallboard',
      order: 14,
      permissions: [
        { name: 'Blocked', description: 'Disable Wallboard Features', permissionKey: 'none' },
        {
          name: 'View',
          description: 'Allow to view Wallboard',
          permissionKey: RolesEnum.PbxWallboardView,
        },
        {
          name: 'View/Edit',
          description: 'Allow to view/modify Wallboard',
          permissionKey: RolesEnum.PbxWallboardView,
        },
        /*{
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Wallboard',
          permissionKey: RolesEnum.PbxWallboardDelete,
        },*/
      ],
    },
    {
      name: 'Extensions',
      description: 'View/Edit Extensions',
      key: ModulesEnum.PbxAccountsModule,
      group: MenuGroupEnum.PbxGroup,
      permissionPrefix: 'PbxAccounts',
      order: 15,
      permissions: [
        {name: 'Blocked', description: 'Disable Pbx Extensions Features', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Pbx Extensions', permissionKey: RolesEnum.PbxAccounts_View},
        {
          name: 'View/Edit Mine Only',
          description: 'Allow to view/modify Pbx Extensions that assigned to user only',
          permissionKey: RolesEnum.PbxAccounts_EditOwn
        },
        {name: 'View/Edit All', description: 'Allow to view/modify all Pbx Extensions', permissionKey: RolesEnum.PbxAccounts_Edit},
        /*{
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Pbx Extensions',
          permissionKey: RolesEnum.PbxAccounts_Delete
        },*/
      ],
    },
    {
      name: 'Groups',
      description: 'View/Edit Groups',
      key: ModulesEnum.PbxGroupsModule,
      group: MenuGroupEnum.PbxGroup,
      permissionPrefix: 'PbxGroups',
      order: 15,
      permissions: [
        {name: 'Blocked', description: 'Disable Pbx Groups Features', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Pbx Groups', permissionKey: RolesEnum.PbxGroups_View},
        {name: 'View/Edit All', description: 'Allow to view/modify all Pbx Groups', permissionKey: RolesEnum.PbxGroups_Edit},
        /*{
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Pbx Groups',
          permissionKey: RolesEnum.PbxGroups_Delete
        },*/
      ],
    },
    {
      name: 'Holiday Greetings',
      description: 'Set up holiday greetings on pbx',
      key: ModulesEnum.PbxHolidayGreetingsModule,
      group: MenuGroupEnum.PbxGroup,
      permissionPrefix: 'PbxHolidayGreetings',
      order: 16,
      permissions: [
        {name: 'Blocked', description: 'Disable Holiday Greetings Features', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view holiday greetings', permissionKey: RolesEnum.PbxHolidayGreetings_View},
        {name: 'View/Edit', description: 'Allow to view/modify holiday greetings', permissionKey: RolesEnum.PbxHolidayGreetings_Edit},
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete holiday greetings',
          permissionKey: RolesEnum.PbxHolidayGreetings_Delete
        },
      ],
    },
    {
      name: 'IP Whitelist',
      description: 'Manage the IP addresses that can access your phone system outside the USA',
      key: ModulesEnum.FirewallModule,
      group: MenuGroupEnum.PbxGroup,
      permissionPrefix: 'Firewall',
      order: 18,
      permissions: [
        {name: 'Blocked', description: 'Disable IP Whitelist', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view IP Whitelist', permissionKey: RolesEnum.Firewall_View},
        {name: 'View/Edit', description: 'Allow to view/modify IP Whitelist', permissionKey: RolesEnum.Firewall_Edit},
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete IP Whitelist',
          permissionKey: RolesEnum.Firewall_Delete
        },
      ]
    },
    {
      name: 'My Pbx',
      description: 'Login to PBX',
      key: ModulesEnum.PbxModule,
      group: MenuGroupEnum.PbxGroup,
      permissionPrefix: 'Pbx',
      order: 19,
      permissions: [
        {name: 'Blocked', description: 'Disable Pbx login Features', permissionKey: 'none'},
        {name: 'Login As User', description: 'Allow to login to pbx console as user', permissionKey: RolesEnum.Pbx_View},
      ],
    },
    {
      name: 'Company Contacts',
      description: 'Manage Company Contacts',
      key: ModulesEnum.ContactModule,
      group: MenuGroupEnum.ContactGroup,
      permissionPrefix: 'Contact',
      order: 16,
      permissions: [
        {name: 'Blocked', description: 'Disable Company Contacts', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Company Contacts', permissionKey: RolesEnum.CompanyContact_View},
        {name: 'View/Edit', description: 'Allow to view/modify Company Contacts', permissionKey: RolesEnum.CompanyContact_Edit},
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Company Contacts',
          permissionKey: RolesEnum.CompanyContact_Delete
        },
      ]
    },
    {
      name: 'Private Contacts',
      description: 'Manage Private Contacts',
      key: ModulesEnum.PrivateContactModule,
      group: MenuGroupEnum.ContactGroup,
      permissionPrefix: 'PrivateContact',
      order: 17,
      permissions: [
        {name: 'Blocked', description: 'Disable Private Contacts', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Private Contacts', permissionKey: RolesEnum.PrivateContact_View},
        {name: 'View/Edit', description: 'Allow to view/modify Private Contacts', permissionKey: RolesEnum.PrivateContact_Edit},
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Private Contacts',
          permissionKey: RolesEnum.PrivateContact_Delete
        },
      ]
    },
    {
      name: 'Tags',
      description: 'Tags module used in contacts',
      key: ModulesEnum.TagsModule,
      group: MenuGroupEnum.TagsGroup,
      permissionPrefix: 'Tags',
      order: 18,
      permissions: [
        {name: 'Blocked', description: 'Disable Tags Features', permissionKey: 'none'},
        {name: 'Tags View', description: 'Allow to view Tags', permissionKey: RolesEnum.Tags_View},
        {name: 'Tags View/Edit', description: 'Allow to view/modify Tags', permissionKey: RolesEnum.Tags_Edit},
        {
          name: 'Tags View/Edit/Delete',
          description: 'Allow to view/modify/delete Tags',
          permissionKey: RolesEnum.Tags_Delete,
        },
      ],
    },
    {
      name: 'Custom Fields',
      description: 'Custom Fields module used in contacts',
      key: ModulesEnum.CustomFieldsModule,
      group: MenuGroupEnum.CustomFieldsGroup,
      permissionPrefix: 'Custom Fields',
      order: 18,
      permissions: [
        {name: 'Blocked', description: 'Disable Custom Fields Features', permissionKey: 'none'},
        {name: 'Custom Fields View', description: 'Allow to view Custom Fields', permissionKey: RolesEnum.CustomFieldsView},
        {name: 'Custom Fields View/Edit', description: 'Allow to view/modify Custom Fields', permissionKey: RolesEnum.CustomFieldsEdit},
        {
          name: 'Custom Fields View/Edit/Delete',
          description: 'Allow to view/modify/delete Custom Fields',
          permissionKey: RolesEnum.CustomFieldsDelete,
        },
      ],
    },
    {
      name: 'TeleGo Mobile App',
      description: 'Manage your TeleGo Mobile App Settings',
      key: ModulesEnum.BasicSoftPhoneModule,
      group: MenuGroupEnum.SoftPhoneGroup,
      permissionPrefix: 'BasicSoftPhone',
      order: 19,
      permissions: [
        {name: 'Blocked', description: 'TeleGo Mobile App Features', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view TeleGo Mobile App information', permissionKey: RolesEnum.BasicSoftPhoneView},
        {name: 'Edit', description: 'Allow to change TeleGo Mobile App password', permissionKey: RolesEnum.BasicSoftPhoneEdit},
      ],
    },
    {
      name: 'TeleGo Desktop App',
      description: 'Manage your TeleGo Desktop App Settings',
      key: ModulesEnum.SoftPhoneModule,
      group: MenuGroupEnum.SoftPhoneGroup,
      permissionPrefix: 'SoftPhone',
      order: 20,
      permissions: [
        {name: 'Blocked', description: 'Desktop App Features', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Desktop App information', permissionKey: RolesEnum.SoftPhoneView},
        {name: 'Edit', description: 'Allow to change Desktop App password', permissionKey: RolesEnum.SoftPhoneEdit},
      ],
    },
    {
      name: 'HHAeXchange',
      description: 'HHAeXchange Integration With Phone System',
      key: ModulesEnum.HhaModule,
      group: MenuGroupEnum.HhaGroup,
      permissionPrefix: 'Hha',
      order: 21,
      permissions: [
        {
          name: 'Blocked',
          description: 'HHAeXchange Features',
          permissionKey: 'none',
        },
        {
          name: 'HHAeXchange View',
          description: 'Allow to view/use HHAeXchange information',
          permissionKey: RolesEnum.HhaView,
        },
        {
          name: 'HHAeXchange Edit',
          description: 'Allow to change HHAeXchange data',
          permissionKey: RolesEnum.HhaEdit,
        },
        {
          name: 'HHAeXchange Delete',
          description: 'Allow to delete HHAeXchange data',
          permissionKey: RolesEnum.HhaDelete,
        },
      ],
    },
    {
      name: 'Events Flow',
      description: 'Manage the Events Flow',
      key: ModulesEnum.EventsFlowModule,
      group: MenuGroupEnum.EventsFlowGroup,
      permissionPrefix: 'EventsFlow',
      order: 22,
      permissions: [
        {name: 'Blocked', description: 'Disable Events Flow', permissionKey: 'none'},
        {name: 'View', description: 'Allow to view Events Flow', permissionKey: RolesEnum.EventsFlowView},
        {name: 'View/Edit', description: 'Allow to view/modify Events Flow', permissionKey: RolesEnum.EventsFlowEdit},
        {
          name: 'View/Edit/Delete',
          description: 'Allow to view/modify/delete Events Flow',
          permissionKey: RolesEnum.EventsFlowDelete
        },
      ]
    },
    {
      name: 'VideoConference',
      description: 'VideoConference configuration module',
      key: ModulesEnum.VideoConferenceModule,
      group: MenuGroupEnum.VideoConferenceGroup,
      permissionPrefix: 'VideoConference',
      order: 25,
      permissions: [
        {name: 'Blocked', description: 'Video Conference Features', permissionKey: 'none'},
        {
          name: 'Video Conference View',
          description: 'Allow to view Video Conference information',
          permissionKey: RolesEnum.VideoConferenceView
        },
        {
          name: 'Video Conference Edit',
          description: 'Allow to create/change Video Conferences',
          permissionKey: RolesEnum.VideoConferenceView
        },
        {
          name: 'Video Conference Delete',
          description: 'Allow to create/change/delete Video Conferences',
          permissionKey: RolesEnum.VideoConferenceView
        },
      ],
    },
  ];

  public static userModules(modules: string[], permissions: AccountRoleItemModel[]): ModuleDefinition[] {

    const modulesDef: ModuleDefinition[] = [];

    console.log(permissions);

    modules.forEach((item: string) => {

      const module = RolesDefinition.allRoles.find((m) => {
        return m.key === item;
      });

      if (module) {
        let anySelected = false;
        let nonePermission: PermissionDefinition;
        if (module.permissions) {
          module.permissions.forEach((x) => {
            const selected = permissions.findIndex(e => e.permissionKey === x.permissionKey) > -1;
            x.selected = selected;
            if (selected) {
              anySelected = true;
            }
            if (x.permissionKey === 'none') {
              nonePermission = x;
            }
          });
        }
        if (nonePermission) {
          nonePermission.selected = !anySelected;
        }

        if (module.additionalPermissions) {
          module.additionalPermissions.forEach((x) => {
            const userPermission = permissions.find(e => e.permissionKey === x.permissionKey);
            if (userPermission) {
              if (x.inputType === 'CheckboxInputComponent') {
                x.value = `${Boolean(userPermission.specialPermission)}`;
              } else {
                x.value = userPermission.specialPermission
              }
            } else {
              x.value = undefined;
            }
          });
        }

        modulesDef.push(module);
      }
    });

    return modulesDef.sort((a, b) => {
      return a.order - b.order;
    });
  }
}
